// A booking may carry up to four related purchasers, not one. The cap and the rules
// for rewriting an opportunity's customer_ids live here rather than in the 1,400-line booking
// page because this repo has no React testing library, so a pure module is the only layer that
// can be tested.
//
// This module makes no country assumption. Gating is the caller's job: pass the limit in.
// The booking-detail screen is NOT itself behind a country flag — its main OPPORTUNITY.READ
// runs everywhere, and only the navigation into it is gated (on TokenPayment, Vietnam only),
// so a direct URL reaches that screen from any tenant. See
// docs/merge-audit-main-svn-to-master.md on gating.

// Equal to MAX_RELATED_PURCHASERS in the buyer site's
// mhub-marketplace-web/src/utils/relatedPurchasers.ts.
export const MAX_RELATED_PURCHASERS = 4;

// The original one-purchaser rule, and the default when a caller names no limit. Defaulting to the
// old behaviour means a caller that forgets to gate degrades to one, never silently to four.
export const LEGACY_RELATED_PURCHASER_LIMIT = 1;

// How many a booking may hold, given whether this tenant has the Vietnam booking concept.
export const relatedPurchaserLimit = (isBookingConcept) => (
  isBookingConcept ? MAX_RELATED_PURCHASERS : LEGACY_RELATED_PURCHASER_LIMIT
);

// The Add control is offered only in a status that allows managing purchasers and below the
// caller's limit.
export const canAddRelatedPurchaser = ({
  count, canModify, max = LEGACY_RELATED_PURCHASER_LIMIT,
}) => Boolean(canModify) && Number(count || 0) < Number(max);

// Order-preserving union of id lists. Falsy ids are dropped and the first occurrence wins.
export const mergeRelatedCustomerIds = (...lists) => {
  const merged = [];
  lists.forEach((list) => (list || []).forEach((id) => {
    if (id && !merged.includes(id)) merged.push(id);
  }));
  return merged;
};

// Merge, never rebuild: whatever the opportunity has stored is carried through untouched, so an
// id this screen does not model — the primary customer's, when it is stored in customer_ids —
// is never dropped by an add. A repeated id is a no-op.
export const appendRelatedCustomerId = (existing, customerId) => (
  mergeRelatedCustomerIds(existing, customerId ? [customerId] : [])
);

// Drops one customer, leaving every other id in place.
export const removeRelatedCustomerId = (existing, customerId) => (
  (existing || []).filter((id) => id && id !== customerId)
);

// The same person must not appear twice on one booking. A booking could be saved with
// the main purchaser's CCCD re-used on a related purchaser, recording one person under two roles.
// The comparison is per identity type and per booking: the same number under a different type is a
// different document, and the same number on another booking is legitimate.
//
// A booking purchaser, as this module sees one, is { customerId, name, identity, identityType }.
// `name` rides along only so a caller can say who already holds the number.
//
// Country gating stays with the caller, as everywhere else in this file. Vietnam is the only
// tenant with these three identity types; Malaysia keeps its number in `nric` and Indonesia in
// `ktp`, which this decoder does not read.

// '1' resolves to the tenant's own IC field so Malaysia keeps reading `nric`. Identical to the
// mapping RegisterCustomer has carried locally.
export const identityFieldForType = (identityType, icField = 'cccd') => {
  const type = String(identityType == null ? '' : identityType);
  if (type === '2') return 'passport';
  if (type === '3') return 'other_identity';
  return icField;
};

const identityValue = (contact, field) => String(
  contact && contact[field] != null ? contact[field] : '',
).trim();

// The one place the CRM identity shape is decoded. A declared type wins; otherwise the type is
// inferred from whichever number is populated. Contacts written by older Showroom syncs hold a
// CCCD in `nric` with no type at all, hence that fallback. Returns '' — never a default of '1' —
// when nothing is on file, so a purchaser with no document can neither block nor be blocked.
export const contactIdentityType = (contact) => {
  if (!contact) return '';
  const declared = String(contact.identity_type || contact.identity_type_id || '').trim();
  if (declared) return declared;
  if (identityValue(contact, 'cccd') || identityValue(contact, 'nric')) return '1';
  if (identityValue(contact, 'passport')) return '2';
  if (identityValue(contact, 'other_identity')) return '3';
  return '';
};

// Works on a stored contact and on RegisterCustomer's `values` alike — both carry the same field
// names, which is what lets the create-a-new-purchaser path reuse this untouched.
export const contactIdentity = (contact) => {
  const identityType = contactIdentityType(contact);
  if (!identityType) return { identityType: '', identity: '' };
  const identity = identityType === '1'
    ? (identityValue(contact, 'cccd') || identityValue(contact, 'nric'))
    : identityValue(contact, identityFieldForType(identityType));
  return { identityType, identity };
};

export const toBookingPurchaser = ({ customerId, name, contact }) => ({
  customerId,
  name: name || (contact && contact.name) || '',
  ...contactIdentity(contact),
});

// The drawer strips nothing from what is typed, and a stored number may carry the spaces or dashes
// a person wrote it with, so both sides are stripped before they are compared.
export const normalizeIdentity = (value) => String(value == null ? '' : value).replace(/[\s-]/g, '');

// The numbers of one identity type already taken on this booking, excluding the purchaser being
// edited. An absent `excludeCustomerId` excludes nobody, which is what an add wants.
export const takenIdentitiesFor = ({ purchasers, identityType, excludeCustomerId }) => {
  const type = String(identityType == null ? '' : identityType);
  if (!type) return [];

  const taken = [];
  (purchasers || []).forEach((purchaser) => {
    if (!purchaser) return;
    if (excludeCustomerId && purchaser.customerId === excludeCustomerId) return;
    if (String(purchaser.identityType == null ? '' : purchaser.identityType) !== type) return;
    const candidate = normalizeIdentity(purchaser.identity);
    if (candidate && !taken.includes(candidate)) taken.push(candidate);
  });
  return taken;
};

// CRM has no Validator class, so this stands in for the buyer site's Validator._isDuplicated.
// The comparison is case-insensitive: only passports are alphanumeric, and that is precisely the
// type where retyping the letters in another case would otherwise side-step the block. A blank
// value is never taken, or the first purchaser with no document on file would block every other.
export const isIdentityTaken = (value, takenIdentities) => {
  const candidate = normalizeIdentity(value).toLowerCase();
  if (!candidate) return false;
  return (takenIdentities || []).some(
    (taken) => normalizeIdentity(taken).toLowerCase() === candidate,
  );
};

// Who on this booking already holds `contact`'s document, or null. Returns the purchaser rather
// than a boolean so the message can name them.
export const findPurchaserWithSameIdentity = ({ purchasers, contact, excludeCustomerId }) => {
  const { identityType, identity } = contactIdentity(contact);
  if (!identityType || !identity) return null;
  const taken = takenIdentitiesFor({ purchasers, identityType, excludeCustomerId });
  if (!isIdentityTaken(identity, taken)) return null;
  const candidate = normalizeIdentity(identity).toLowerCase();
  return (purchasers || []).find((purchaser) => (
    purchaser
    && !(excludeCustomerId && purchaser.customerId === excludeCustomerId)
    && String(purchaser.identityType == null ? '' : purchaser.identityType) === identityType
    && normalizeIdentity(purchaser.identity).toLowerCase() === candidate
  )) || null;
};
